// src/Lexbuild/AgentFlowEditor.jsx

import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
  } from 'react';
  import ReactFlow, {
    addEdge,
    applyNodeChanges,
    applyEdgeChanges,
    Controls,
    Background,
    Handle,
    Position,
  } from 'reactflow';
  import 'reactflow/dist/style.css';
  import './AgentFlowEditor.css';
  
  const NODE_TYPES = [
    { type: 'httpRequest',  label: '🌐 HTTP Request' },
    { type: 'parseHTML',    label: '🖋️ Parse HTML'   },
    { type: 'extractHTML',  label: '🔍 Extract HTML' },
    { type: 'llmStep',      label: '🤖 LLM Call'     },
    { type: 'parseJSON',    label: '🗄️ Parse JSON'   },
    { type: 'extractField', label: '🔍 Extract Field' },
    { type: 'sendToUser',   label: '📤 Output Result' },
  ];
  
  const handleStyle = {
    width: 16,
    height: 16,
    background: '#fff',
    border: '2px solid #2563eb',
  };



  
  const nodeTypes = {
    httpRequest: ({ data }) => (
      <div className="flow-node">
        <Handle type="target" position={Position.Top} style={handleStyle} />
        🌐 HTTP Request
        <div className="node-preview">{data.url || 'no URL set'}</div>
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      </div>
    ),
    parseHTML: () => (
      <div className="flow-node">
        <Handle type="target" position={Position.Top} style={handleStyle} />
        🖋️ Parse HTML
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      </div>
    ),
    extractHTML: ({ data }) => (
      <div className="flow-node">
        <Handle type="target" position={Position.Top} style={handleStyle} />
        🔍 Extract HTML
        <div className="node-preview">
          {data.selector || 'selector'}
          {data.attr ? ` @${data.attr}` : ''}
        </div>
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      </div>
    ),
    llmStep: ({ data }) => (
      <div className="flow-node">
        <Handle type="target" position={Position.Top} style={handleStyle} />
        🤖 LLM Call
        <div className="node-preview">{data.prompt || 'prompt'}</div>
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      </div>
    ),
    parseJSON: () => (
      <div className="flow-node">
        <Handle type="target" position={Position.Top} style={handleStyle} />
        🗄️ Parse JSON
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      </div>
    ),
    extractField: ({ data }) => (
      <div className="flow-node">
        <Handle type="target" position={Position.Top} style={handleStyle} />
        🔍 Extract Field
        <div className="node-preview">{data.field || 'field'}</div>
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      </div>
    ),
    sendToUser: () => (
      <div className="flow-node">
        <Handle type="target" position={Position.Top} style={handleStyle} />
        📤 Output Result
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      </div>
    ),
  };
  
  export default function AgentFlowEditor({
    initialNodes    = [],
    initialEdges    = [],
    initialWorkflow,
    onSave,
    onCancel,
  }) {

    const [panelOpen, setPanelOpen] = useState(true);

    // safety for legacy workflows
    const safeWorkflow = Array.isArray(initialWorkflow) ? initialWorkflow : [];
  
    const [nodes, setNodes]         = useState([]);
    const [edges, setEdges]         = useState([]);
    const [selected, setSelected]   = useState(null);
    const [rfInstance, setRfInstance] = useState(null);
    const initializedRef            = useRef(false);
  
    // build legacy nodes once
    const legacyNodes = useMemo(
      () =>
        safeWorkflow.map((step, i) => ({
          id:       `${Date.now()}-${i}`,
          type:     step.step,
          position: { x: 50, y: 50 + i * 80 },
          data:     { ...step.params },
        })),
      [safeWorkflow]
    );
  
    // initialize nodes/edges exactly once
    useEffect(() => {
      if (initializedRef.current) return;
      if (initialNodes.length > 0) {
        setNodes(initialNodes);
      } else {
        setNodes(legacyNodes);
      }
      setEdges(initialEdges);
      initializedRef.current = true;
    }, [initialNodes, initialEdges, legacyNodes]);
  
    // whenever instance or node count changes, fit view
    useEffect(() => {
      if (rfInstance) {
        rfInstance.fitView({ padding: 0.2 });
      }
    }, [rfInstance, nodes.length]);
  
    // React Flow handlers
    const onNodesChange = useCallback(
      (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
      []
    );
    const onEdgesChange = useCallback(
      (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
      []
    );
    const onConnect = useCallback(
      (conn) => setEdges((eds) => addEdge(conn, eds)),
      []
    );
  
    // palette drag/drop
    const onPaletteDragStart = (e, type) => {
      e.dataTransfer.setData('application/reactflow', type);
      e.dataTransfer.effectAllowed = 'move';
    };
    const onDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };
    const onDrop = useCallback(
      (e) => {
        e.preventDefault();
        if (!rfInstance) return;
        const type = e.dataTransfer.getData('application/reactflow');
        if (!type) return;
        const { clientX: x, clientY: y } = e;
        const pos = rfInstance.screenToFlowPosition({ x, y });
        const newNode = { id: `${Date.now()}`, type, position: pos, data: {} };
        setNodes((nds) => [...nds, newNode]);
        setSelected(newNode);
        setTimeout(() => rfInstance.fitView({ padding: 0.2 }), 50);
      },
      [rfInstance]
    );
  
    // update node data
    const updateNodeData = (id, upd) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...upd } } : n))
      );
      if (selected?.id === id) {
        setSelected((n) => ({ ...n, data: { ...n.data, ...upd } }));
      }
    };
  
    // save callback
    const handleSave = () => {
      const workflow = nodes.map((n) => ({
        step:   n.type,
        params: { ...n.data },
      }));
      onSave({ nodes, edges, workflow });
    };
  
    return (
      <div className="flow-editor-wrapper">
        {/* Palette */}
        <aside className="flow-sidebar">
          {NODE_TYPES.map(({ type, label }) => (
            <div
              key={type}
              className="flow-node-btn"
              draggable
              onDragStart={(e) => onPaletteDragStart(e, type)}
            >
              {label}
            </div>
          ))}
        </aside>
  
        {/* Canvas */}
        <div className="flow-canvas" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={{ style: { strokeWidth: 4 } }}
            onInit={setRfInstance}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => setSelected(node)}
            fitView
            snapToGrid
            snapGrid={[20, 20]}
            style={{ width: '100%', height: '100%' }}
          >
            <Controls position="top-left" />
            <Background gap={20} size={2} />
          </ReactFlow>
        </div>
  
        {/* Settings Panel */}
       
        <div className={`node-settings${panelOpen ? '' : ' collapsed'}`}>
        {/* ← flap you click to collapse/expand */}
        <div
          className="toggle"
          onClick={() => setPanelOpen((o) => !o)}
        >
          {panelOpen ? '←' : '→'}
        </div>

          {selected ? (
            <>
              <div className="settings-header">
                <h4>{selected.type} Settings</h4>
                <div className="settings-actions">
              
                </div>
              </div>
  
              {selected.type === 'httpRequest' && (
                <label>
                  URL:
                  <input
                    value={selected.data.url || ''}
                    onChange={(e) =>
                      updateNodeData(selected.id, { url: e.target.value })
                    }
                  />
                </label>
              )}
              {selected.type === 'parseHTML' && <p>No configuration needed.</p>}
              {selected.type === 'extractHTML' && (
                <>
                  <label>
                    Selector:
                    <input
                      value={selected.data.selector || ''}
                      onChange={(e) =>
                        updateNodeData(selected.id, {
                          selector: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Attribute:
                    <input
                      value={selected.data.attr || ''}
                      onChange={(e) =>
                        updateNodeData(selected.id, { attr: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Output field:
                    <input
                      value={selected.data.field || ''}
                      onChange={(e) =>
                        updateNodeData(selected.id, { field: e.target.value })
                      }
                    />
                  </label>
                </>
              )}
              {selected.type === 'llmStep' && (
                <label>
                  Prompt:
                  <textarea
                    rows={3}
                    value={selected.data.prompt || ''}
                    onChange={(e) =>
                      updateNodeData(selected.id, { prompt: e.target.value })
                    }
                  />
                </label>
              )}
              {selected.type === 'parseJSON' && <p>No configuration needed.</p>}
              {selected.type === 'extractField' && (
                <label>
                  Field name:
                  <input
                    value={selected.data.field || ''}
                    onChange={(e) =>
                      updateNodeData(selected.id, { field: e.target.value })
                    }
                  />
                </label>
              )}
              {selected.type === 'sendToUser' && <p>No configuration needed.</p>}
            </>
          ) : (
            <p>Select a node to edit its settings</p>
          )}
        </div>
      </div>
    );
  }
  